import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, Scale, MessagesSquare, ArrowRight, Lock, Users, HelpCircle,
  ShieldAlert, TriangleAlert, CircleHelp, Clock, Building2, HeartHandshake,
  FileText, CheckCircle2,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { CATEGORIES } from "@/lib/constants";
import { useContent } from "@/context/ContentContext";

const ICONS = { Users, HelpCircle, ShieldAlert, Scale, TriangleAlert, MessagesSquare, CircleHelp };

const HERO_IMG =
  "https://images.unsplash.com/photo-1631560791099-9143513a4fbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const MEETING_IMG =
  "https://images.unsplash.com/photo-1507537362848-9c7e70b7b5c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const STEPS = [
  { icon: MessagesSquare, title: "Share your concern", text: "Submit through this portal, by email, or by phone — anonymously if you prefer." },
  { icon: FileText, title: "We acknowledge it", text: "You receive a case number and access code to track progress anytime." },
  { icon: Users, title: "A specialist reviews", text: "A trained ER specialist triages the concern and determines next steps." },
  { icon: CheckCircle2, title: "Tracked to resolution", text: "We coordinate a fair response and keep you informed to resolution." },
];

const TRUST = [
  { icon: Lock, label: "Confidential" },
  { icon: Scale, label: "Impartial" },
  { icon: Clock, label: "Available anytime" },
  { icon: Building2, label: "Every campus" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { content } = useContent();

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 rounded-full bg-forest-50 border border-forest-200 px-4 py-1.5 text-xs font-bold tracking-[0.15em] uppercase text-forest-700"
            >
              <Shield className="h-3.5 w-3.5" strokeWidth={1.5} /> {content.hero.badge}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest-900 leading-[1.05]"
            >
              {content.hero.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
              className="mt-6 text-base sm:text-lg text-charcoal-600 leading-relaxed max-w-xl"
            >
              {content.hero.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Button
                onClick={() => navigate("/report")}
                data-testid="hero-share-concern-btn"
                className="rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white px-7 py-6 text-base shadow-lg hover:-translate-y-0.5 transition-transform"
              >
                Share a Concern <ArrowRight className="ml-1 h-4 w-4" strokeWidth={2} />
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate("/resources")}
                data-testid="hero-resources-btn"
                className="rounded-full bg-bone-100 hover:bg-bone-200 text-forest-800 border border-forest-600/10 px-7 py-6 text-base"
              >
                Explore Resources
              </Button>
            </motion.div>

            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3">
              {TRUST.map((t) => (
                <span key={t.label} className="inline-flex items-center gap-2 text-sm text-charcoal-600">
                  <t.icon className="h-4 w-4 text-forest-500" strokeWidth={1.5} /> {t.label}
                </span>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_24px_60px_rgba(44,74,59,0.18)] border border-forest-600/10">
              <img src={HERO_IMG} alt="A calm, welcoming Patria campus" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-900/45 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-2xl shadow-xl border border-forest-600/10 p-5 max-w-[240px]">
              <div className="flex items-center gap-2 text-forest-700">
                <HeartHandshake className="h-5 w-5 text-terracotta-400" strokeWidth={1.5} />
                <span className="font-serif text-lg font-semibold">You're not alone</span>
              </div>
              <p className="mt-1.5 text-sm text-charcoal-600">Reach out in addition to — or instead of — your supervisor.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-bone-100/60 border-y border-forest-600/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-3xl overflow-hidden shadow-lg border border-forest-600/10 order-2 lg:order-1">
            <img src={MEETING_IMG} alt="A supportive conversation" className="w-full h-[380px] object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">{content.mission.eyebrow}</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-forest-900 leading-snug">
              {content.mission.title}
            </h2>
            <p className="mt-5 text-charcoal-600 leading-relaxed">
              {content.mission.body}
            </p>
            <ul className="mt-6 space-y-3">
              {(content.mission.bullets || []).map((t) => (
                <li key={t} className="flex items-start gap-3 text-charcoal-700">
                  <CheckCircle2 className="h-5 w-5 text-forest-500 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="max-w-2xl">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">What you can bring</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-forest-900">Any concern is welcome here.</h2>
          <p className="mt-4 text-charcoal-600">Choose whatever feels closest — a specialist will help from there.</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((c, i) => {
            const Icon = ICONS[c.icon] || CircleHelp;
            return (
              <motion.button
                key={c.value}
                onClick={() => navigate(`/report?category=${c.value}`)}
                data-testid={`category-card-${c.value}`}
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group text-left bg-white rounded-2xl border border-forest-600/10 p-6 shadow-[0_4px_24px_rgba(44,74,59,0.04)] hover:shadow-[0_12px_36px_rgba(44,74,59,0.1)] hover:-translate-y-1 transition-all"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-forest-50 text-forest-600 group-hover:bg-terracotta-50 group-hover:text-terracotta-500 transition-colors">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </span>
                <h3 className="mt-4 font-serif text-xl text-forest-900">{c.label}</h3>
                <p className="mt-1.5 text-sm text-charcoal-600 leading-relaxed">{c.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-terracotta-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  Start here <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </span>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-forest-900 text-bone">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-300">The process</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl">What happens when you reach out.</h2>
          </div>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-11 w-11 rounded-full bg-terracotta-400 text-white font-serif text-lg font-semibold">{i + 1}</span>
                  <s.icon className="h-5 w-5 text-bone/60" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-serif text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-bone/70 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Policies preview */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">Know your protections</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-forest-900">Policies & resources</h2>
          </div>
          <Link to="/resources" className="inline-flex items-center gap-1 text-sm font-semibold text-forest-700 hover:text-terracotta-500 transition-colors">
            View all resources <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {[
            { slug: "non-harassment", title: "Non-Harassment", icon: ShieldAlert, text: "A workplace free from harassment of any kind." },
            { slug: "non-discrimination", title: "Non-Discrimination", icon: Scale, text: "Equal opportunity and fair treatment for all." },
            { slug: "anti-retaliation", title: "Anti-Retaliation", icon: Shield, text: "Protection for everyone who speaks up in good faith." },
          ].map((p) => (
            <Link
              key={p.slug}
              to={`/resources/${p.slug}`}
              data-testid={`policy-preview-${p.slug}`}
              className="group bg-white rounded-2xl border border-forest-600/10 p-7 shadow-[0_4px_24px_rgba(44,74,59,0.04)] hover:-translate-y-1 hover:shadow-lg transition-all"
            >
              <p.icon className="h-7 w-7 text-terracotta-400" strokeWidth={1.5} />
              <h3 className="mt-4 font-serif text-xl text-forest-900">{p.title}</h3>
              <p className="mt-2 text-sm text-charcoal-600">{p.text}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-forest-700 group-hover:gap-2 transition-all">
                Read policy <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        <div className="rounded-3xl bg-terracotta-400 text-white p-10 sm:p-14 grid lg:grid-cols-2 gap-8 items-center shadow-xl">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug">Ready to talk? We're here whenever you are.</h2>
            <p className="mt-4 text-white/90">You may reach the ER CoE directly — confidentially, and at any time.</p>
            <div className="mt-6 space-y-1.5 text-white/90 text-sm">
              <div>Email: {content.contact.email}</div>
              <div>Phone: {content.contact.phone}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 lg:justify-end">
            <Button onClick={() => navigate("/report")} data-testid="cta-share-concern-btn" className="rounded-full bg-white text-terracotta-600 hover:bg-bone px-7 py-6 text-base font-semibold hover:-translate-y-0.5 transition-transform">
              Share a Concern
            </Button>
            <Button onClick={() => navigate("/track")} variant="secondary" className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white border-0 px-7 py-6 text-base">
              Track a Concern
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
